import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { isFunction } from 'lodash';
import { Plus } from 'lucide-react';
import React, { Dispatch, SetStateAction, useState } from 'react';

import { Button } from '@/components/button';
import { Link } from '@/components/link';
import { Landing } from '@/components/modules/landing';
import { ShipmentManageFilters } from '@/components/modules/shipment/manage-filters-drawer';
import { ShipmentsTable } from '@/components/modules/shipment/table';
import { DEFAULT_SHIPMENTS_TAB, ShipmentsTabs } from '@/components/modules/shipment/tabs';
import { TabsEditDialog } from '@/components/modules/shipment/tabs/edit-dialog';
import { CreateShipmentListTabFormData } from '@/components/modules/shipment/tabs/form';
import { FilterData } from '@/components/ui/data-table/filter-toolbar';
import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { UserMessage } from '@/components/ui/user-message';
import { useDebounce } from '@/hooks/use-debounce';
import { useDrawer } from '@/hooks/use-drawer';
import { useSavedShipmentViews } from '@/hooks/use-saved-shipment-views';
import { useUserSettings } from '@/hooks/use-user-settings';
import { SAVED_ITEM_TYPE, TRACKING_IMPORT_URL, TRACKING_REQUESTS_URL } from '@/lib/constants';
import { SavedItemModel, SavedShipmentFiltersData } from '@/lib/models/saved-item/types';
import { byLabel } from '@/lib/models/saved-item/utils';
import { ShipmentGrouping, ShipmentModel } from '@/lib/models/shipment/types';
import { TableLayout } from '@/types';
import { tv } from '@/utils/styles';

export interface ShipmentListProps {
  shipments?: ShipmentModel[];
  onClickDetails: (value: ShipmentModel) => void;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pagination: PaginationState;
  pageCount: number;
  isLoading?: boolean;
  error?: string;
  onSortingChange?: (value: SortingState) => void;
  sorting?: SortingState;
  filterData?: FilterData;
  onColumnFiltersChange?: (value: ColumnFiltersState) => void;
  columnFilters?: ColumnFiltersState;
  hasAppliedFilters?: boolean;
  onUpdateShipmentTags?: (args: { labels: string[]; shipmentId: string }) => Promise<void>;
  selectedTab?: string;
  onSelectedTabChange?: (value: string) => void;
  layout?: TableLayout;
  onLayoutChange?: (layout: TableLayout) => void;
  grouping?: ShipmentGrouping;
  onGroupingChange?: (grouping: ShipmentGrouping) => void;
  isTableSettingsOpen?: boolean;
}

const ShipmentList = ({
  shipments = [],
  onClickDetails,
  error,
  isLoading,
  onPaginationChange,
  pagination,
  pageCount,
  onSortingChange,
  sorting,
  filterData,
  onColumnFiltersChange,
  columnFilters,
  hasAppliedFilters,
  onUpdateShipmentTags,
  selectedTab,
  onSelectedTabChange,
  layout,
  onLayoutChange,
  grouping,
  onGroupingChange,
  isTableSettingsOpen = false,
}: ShipmentListProps) => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSharingView, setIsSharingView] = useState(false);
  const { isOpen: isManageDrawerOpen, openDrawer, closeDrawer } = useDrawer();
  const [areFiltersVisible, setAreFiltersVisible] = useState(true);

  const shouldShowLanding = !hasAppliedFilters && shipments?.length === 0;
  const filterState: SavedShipmentFiltersData = {
    columnFilters,
    sorting,
    ...(layout ? { layout } : {}),
  };

  const { savedShipmentViews, create, update, del } = useSavedShipmentViews();
  const { updateUserSettings } = useUserSettings();

  const selectedFilter = savedShipmentViews ? savedShipmentViews.find(byLabel(selectedTab)) : null;
  const hasFilterChanged: boolean = selectedFilter
    ? selectedFilter.data !== JSON.stringify(filterState)
    : selectedTab === DEFAULT_SHIPMENTS_TAB && (Boolean(columnFilters?.length) || Boolean(sorting?.length));
  const saveFiltersLabel = selectedFilter ? 'Save changes' : 'Save as new tab';

  // Required due to filters taking a couple of render cycles to settle
  const debounceHasFilterChanged = useDebounce(hasFilterChanged, 250);

  const handleDialogOpen = () => {
    setIsCreateDialogOpen(true);
  };

  const handleSubmit = async ({ label }: CreateShipmentListTabFormData) => {
    const existingLabel = savedShipmentViews.find(byLabel(label));
    if (existingLabel) {
      throw new Error(`A tab called ${label} already exists`);
    }

    const savedItem: SavedItemModel = {
      label,
      type: SAVED_ITEM_TYPE.SHIPMENT,
      data: JSON.stringify(filterState),
    };

    await create(savedItem);
    setIsCreateDialogOpen(false);
    if (isFunction(onSelectedTabChange)) {
      onSelectedTabChange(label);
    }
  };

  const handleUpdate = async (item: SavedItemModel) => {
    const existingLabel = savedShipmentViews.find(byLabel(item.label));
    if (existingLabel) {
      throw new Error(`A tab called ${item.label} already exists`);
    }

    await update(item);

    // If the user is changing the label of the selected tab, change tabs
    if (selectedFilter?.id === item.id && isFunction(onSelectedTabChange)) {
      onSelectedTabChange(item.label);
    }
  };

  const handleUpdateFilters = async () => {
    if (selectedFilter) {
      return update({ ...selectedFilter, data: JSON.stringify(filterState) });
    }
    // User has clicked create new tab
    return handleDialogOpen();
  };

  const handleResetFilters = () => {
    if (selectedFilter) {
      handleClickTab(selectedFilter);
    }
  };

  const handleClickTab = (savedItem?: SavedItemModel) => {
    const {
      sorting: savedSorting,
      columnFilters: savedColumnFilters,
      layout: savedLayout,
    }: SavedShipmentFiltersData = savedItem ? JSON.parse(savedItem.data) : {};
    if (onColumnFiltersChange) {
      onColumnFiltersChange(savedColumnFilters || []);
    }
    if (onSortingChange) {
      onSortingChange(savedSorting || []);
    }
    if (onLayoutChange && savedLayout) {
      onLayoutChange(savedLayout);
    }
  };

  // When user clicks an item from inside the manage drawer
  const handleClickManageItem = (item: SavedItemModel) => {
    handleClickTab(item);
    if (onSelectedTabChange && item.label) {
      onSelectedTabChange(item.label);
    }
    closeDrawer();
  };

  const handleDelete = async (item: SavedItemModel) => {
    if (item.label === selectedTab && isFunction(onSelectedTabChange)) {
      // Select default tab
      onSelectedTabChange(DEFAULT_SHIPMENTS_TAB);
    }
    await del(item);
  };

  const handleReorder = async (items: SavedItemModel[]) => {
    const newSorting = items.map((item) => item.id || '');
    await updateUserSettings({ shipmentViewSorting: { value: newSorting } });
  };

  const handleShareView = () => {
    setIsSharingView(true);
    setIsCreateDialogOpen(true);
  };

  const handleEditOpenChange = (value: boolean) => {
    if (!value) {
      setIsSharingView(false);
    }
    setIsCreateDialogOpen(value);
  };

  const { scroller, contentWrapper } = styles();

  if (error)
    return (
      <UserMessage className="self-start" heading={error} body="Please try refreshing your browser." variant="error" />
    );

  if (shouldShowLanding)
    return (
      <Landing
        heading="Fluent Cargo subscribers can now track shipments through this platform"
        subHeading="Add a shipment now using an MBOL, AWB or Container ID to start tracking it."
        footerText={
          <>
            If you have recently added a shipment, it might still be processing.{' '}
            <Link href={TRACKING_REQUESTS_URL} className="underline hover:text-lightBlue-600">
              See pending shipments.
            </Link>
          </>
        }
        actions={
          <Button href={TRACKING_IMPORT_URL} iconLeft={<Plus />}>
            Add a shipment
          </Button>
        }
        imageUrl="https://www.datocms-assets.com/52833/1722390636-flc-tracking.png"
        imageAlt="shipment tracking"
      />
    );

  return (
    <div className={scroller()}>
      <div className={contentWrapper()}>
        <ShipmentsTabs
          savedItems={savedShipmentViews}
          currentTab={selectedTab}
          onTabClick={handleClickTab}
          onValueChange={onSelectedTabChange}
          onClickCreate={handleDialogOpen}
          onClickSettings={openDrawer}
          onClickFilters={() => setAreFiltersVisible(!areFiltersVisible)}
          onClickShare={handleShareView}
        />
        <ShipmentsTable
          onClickDetails={onClickDetails}
          data={shipments}
          onPaginationChange={onPaginationChange}
          pagination={pagination}
          pageCount={pageCount}
          onSortingChange={onSortingChange}
          sorting={sorting || []}
          filterData={filterData}
          onColumnFiltersChange={onColumnFiltersChange}
          columnFilters={columnFilters}
          onUpdateShipmentTags={onUpdateShipmentTags}
          shouldShowSaveFilters={hasFilterChanged ? debounceHasFilterChanged : hasFilterChanged}
          shouldShowResetFilters={hasFilterChanged ? debounceHasFilterChanged : hasFilterChanged}
          onSaveFilters={handleUpdateFilters}
          saveFiltersLabel={saveFiltersLabel}
          onResetFilters={selectedFilter ? handleResetFilters : undefined}
          areFiltersVisible={areFiltersVisible}
          layout={layout}
          onLayoutChange={onLayoutChange}
          grouping={grouping}
          onGroupingChange={onGroupingChange}
          isTableSettingsOpen={isTableSettingsOpen}
        />
        <TabsEditDialog
          isOpen={isCreateDialogOpen}
          onOpenChange={handleEditOpenChange}
          onSubmit={handleSubmit}
          savedItem={isSharingView ? selectedFilter || undefined : undefined}
        />
        <ShipmentManageFilters
          isOpen={isManageDrawerOpen}
          onOpenChange={(value) => (value ? openDrawer() : closeDrawer())}
          savedItems={savedShipmentViews}
          onClickEdit={handleUpdate}
          onClickDelete={handleDelete}
          onClickItem={handleClickManageItem}
          onReorder={handleReorder}
        />
        {isLoading ? <LoadingSpinner isVisible /> : null}
      </div>
      {isLoading && shipments?.length ? <LoadingSpinner isVisible /> : null}
    </div>
  );
};

const styles = tv({
  slots: {
    scroller: 'overflow-auto',
    loadingWrapper: 'grid gap-2 p-4',
    contentWrapper: 'p-4 pt-0',
  },
});

export { ShipmentList };
